import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * 列表页多选 (2026-09-10 批量操作配套).
 *
 * 选中只在当前这一页有效: resetKey (页码 / 筛选条件的组合) 一变就清空。
 * 跨页累积看着方便, 但用户翻两页改一次筛选后早已记不清选了什么, 批量删除按下去就是事故 ——
 * "所见即所选" 是这里刻意的取舍。
 *
 * 跨页模式 (opts.crossPage, 2026-10-13 SKU 库按勾选导出): 翻页保留已选, resetKey 只拼筛选条件 (不含页码),
 * 改筛选才清空; selectedKeys / count 为全部已选 (含其它页), 页面需把"含其它页"的条数展示出来, 批量操作确认弹窗也会列明.
 * 表头全选只作用于当前页 (勾上 = 把本页并入已选, 取消 = 只移出本页).
 *
 * @param keys 当前页所有行的业务键 (顺序与展示一致)
 * @param resetKey 变化即清空选择的依赖串 (页码 + 各筛选值拼起来; 跨页模式下不含页码)
 */
export function useBatchSelect(keys: string[], resetKey: string, opts?: { crossPage?: boolean }) {
    const crossPage = !!opts?.crossPage;
    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
        setSelected(new Set());
    }, [resetKey]);

    // 行可能因为删除/刷新消失: 选中集合始终收敛到当前页仍存在的行, 免得对已经不在的键发批量请求.
    // 跨页模式保留其它页的键 (批量操作完成后由 onClear 清空, 不会残留已删行).
    const pageSelected = useMemo(() => keys.filter((k) => selected.has(k)), [keys, selected]);
    const selectedKeys = useMemo(
        () => (crossPage ? [...pageSelected, ...[...selected].filter((k) => !keys.includes(k))] : pageSelected),
        [crossPage, pageSelected, selected, keys],
    );

    const toggle = useCallback((key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const clear = useCallback(() => setSelected(new Set()), []);

    const allChecked = keys.length > 0 && pageSelected.length === keys.length;

    const toggleAll = useCallback(() => {
        setSelected((prev) => {
            const everySelected = keys.length > 0 && keys.every((k) => prev.has(k));
            if (!crossPage) return everySelected ? new Set() : new Set(keys);
            const next = new Set(prev);
            keys.forEach((k) => (everySelected ? next.delete(k) : next.add(k)));
            return next;
        });
    }, [keys, crossPage]);

    return {
        /** 被选中的键 (当前页按展示顺序在前; 跨页模式含其它页) */
        selectedKeys,
        /** 某行是否选中 */
        isSelected: (key: string) => selected.has(key),
        toggle,
        toggleAll,
        clear,
        /** 表头全选框的勾选态 */
        allChecked,
        /** 表头全选框的半选态 (部分选中) */
        someChecked: pageSelected.length > 0 && !allChecked,
        count: selectedKeys.length,
        /** 不在当前页的已选条数 (仅跨页模式可能非 0) */
        otherPageCount: selectedKeys.length - pageSelected.length,
    };
}

export type BatchSelect = ReturnType<typeof useBatchSelect>;
